import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function HeroMediaTab() {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/hero-video").then((r) => setStatus(r.data)).catch(() => toast.error("Failed to load hero media"));
  }, []);
  useEffect(load, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/admin/hero-video", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      toast.success("Hero video updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the hero video? The editorial photograph will be used instead.")) return;
    await api.delete("/admin/hero-video").catch(() => toast.error("Remove failed"));
    toast.success("Hero video removed");
    load();
  };

  const streamUrl = status?.active ? `${process.env.REACT_APP_BACKEND_URL}${status.url}` : null;

  return (
    <div data-testid="admin-hero-media" className="max-w-3xl">
      <div className="border border-white/8 bg-[#111116] p-6 sm:p-8">
        <p className="eyebrow mb-3">Homepage Hero Background</p>
        <p className="text-sm leading-relaxed text-[#A19E95]">
          Upload a cinematic background video (mp4, webm or mov — under 100 MB). It plays automatically, muted
          and on loop, behind the hero headline beneath the dark overlay. Removing it restores the editorial
          photograph.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={upload} className="hidden" data-testid="admin-hero-file-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="admin-hero-upload-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#08080A] transition-colors hover:bg-[#E5C97A] disabled:opacity-50"
          >
            <Upload size={14} /> {uploading ? "Uploading..." : status?.active ? "Replace Video" : "Upload Video"}
          </button>
          {status?.active && (
            <button
              onClick={remove}
              data-testid="admin-hero-remove-btn"
              className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#A19E95] transition-colors hover:border-[#E74C3C]/50 hover:text-[#E74C3C]"
            >
              <Trash2 size={14} /> Remove Video
            </button>
          )}
        </div>
        {status?.active && streamUrl && (
          <div className="mt-7">
            <p className="label-lux">Current Video</p>
            <video key={streamUrl} src={streamUrl} controls muted loop playsInline className="aspect-video w-full border border-[#D4AF37]/25 bg-black object-cover" data-testid="admin-hero-preview" />
            <p className="mt-2 font-mono text-[10px] text-[#6E6B63]">Updated {status.updated_at}</p>
          </div>
        )}
        {status && !status.active && (
          <p className="mt-6 text-sm text-[#6E6B63]">No video set — the default editorial photograph is in use.</p>
        )}
      </div>
    </div>
  );
}
